/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect } from 'react';

import Button, { IconButton } from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import PageIcon from '@atlaskit/icon/core/page';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { ConfluenceIcon } from '@atlaskit/logo';
import { Main } from '@atlaskit/navigation-system/layout/main';
import { Root } from '@atlaskit/navigation-system/layout/root';
import { Anchor, Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	PanelActionBack,
	PanelActionClose,
	PanelActionGroup,
	PanelActionMore,
	PanelActionNewTab,
} from '@atlassian/panel-system';
import { LayoutWithPanel } from '@atlassian/panel-system/layout-with-panel';
import { PanelBody } from '@atlassian/panel-system/panel-body';
import { PanelContainer } from '@atlassian/panel-system/panel-container';
import { PanelContent } from '@atlassian/panel-system/panel-content';
import { PanelDisclaimer } from '@atlassian/panel-system/panel-disclaimer';
import { PanelFooter } from '@atlassian/panel-system/panel-footer';
import { PanelHeader } from '@atlassian/panel-system/panel-header';
import { usePanelActions } from '@atlassian/panel-system/panel-manager';
import { PanelProvider } from '@atlassian/panel-system/panel-provider';
import { PanelSubheader } from '@atlassian/panel-system/panel-subheader';
import { PanelTitle } from '@atlassian/panel-system/panel-title';

import { PanelCoverImage } from '../images/panel-cover';
import { RelayEnvironmentWrapper } from '../utils/relay-environment-wrapper';

const styles = cssMap({
	mainContent: {
		flex: 1,
		paddingBlockStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlockEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
});

export function SidePanelExample(): JSX.Element {
	return (
		<PanelProvider>
			<RelayEnvironmentWrapper>
				<Root>
					<Main>
						<AppContent />
					</Main>
				</Root>
			</RelayEnvironmentWrapper>
		</PanelProvider>
	);
}

const AppContent = () => {
	const manager = usePanelActions();

	useEffect(() => {
		// Note: You should use <Trigger/> with EntryPoints for optimal performance instead of calling replacePanel
		manager.replacePanel('confluence-panel', <Panel />);
	}, [manager]);

	return (
		<LayoutWithPanel>
			<Box xcss={styles.mainContent}>
				<Stack space="space.200">
					<Heading size="medium">Main content area</Heading>
					<Text>
						Panel system is designed to be rendered inside the navigation system's `Panel` slot.
						Compose your panel with `PanelContainer` and place it inside `Panel` so it sits
						alongside the page's `Main` content with consistent layout and resize behaviour.
					</Text>
				</Stack>
			</Box>
		</LayoutWithPanel>
	);
};

const Panel = () => {
	return (
		<PanelContainer testId="preview-panel">
			<PanelHeader>
				<PanelActionBack onClick={() => {}} testId="back-action" />
				<PanelTitle icon={<ConfluenceIcon shouldUseNewLogoDesign size="xsmall" />}>
					Confluence
				</PanelTitle>
				<PanelActionGroup>
					<PanelActionNewTab
						href="/projects/blueshift"
						onClick={() => {}}
						testId="new-tab-action"
					/>
					<PanelActionMore onClick={() => {}} testId="more-action" />
					<PanelActionClose onClick={() => {}} testId="close-action" />
				</PanelActionGroup>
			</PanelHeader>
			<PanelContent>
				<PanelSubheader
					title="Subheading title"
					titleIcon={<PageIcon label="" />}
					coverImage={<PanelCoverImage />}
				/>
				<PanelBody>
					<Stack space="space.200">
						<Heading size="small" as="h3">
							Project overview
						</Heading>
						<Text>
							Panel system is a set of platform components to standardize the look and feel of
							panels in Atlassian products.
						</Text>
						<Heading size="xsmall" as="h4">
							Key features
						</Heading>
						<Box as="ul">
							<Box as="li">Unified design system components</Box>
							<Box as="li">Consistent navigation patterns</Box>
							<Box as="li">Improved accessibility standards</Box>
						</Box>
						<Heading size="xsmall" as="h4">
							Long content (this should wrap)
						</Heading>
						<Anchor href="https://example.atlassian.net/browse/longnamelongnamelongnamelongnamelongnamelongnamelongnamelongnamelongname">
							https://example.atlassian.net/browse/longnamelongnamelongnamelongnamelongnamelongnamelongnamelongnamelongname
						</Anchor>
					</Stack>
				</PanelBody>
			</PanelContent>
			<PanelFooter
				disclaimer={
					<PanelDisclaimer>
						<Flex justifyContent="space-between" alignItems="center">
							<Flex gap="space.025">
								<IconButton
									icon={ThumbsUpIcon}
									label="Helpful"
									appearance="subtle"
									spacing="compact"
									onClick={() => {}}
								/>
								<IconButton
									icon={ThumbsDownIcon}
									label="Not helpful"
									appearance="subtle"
									spacing="compact"
									onClick={() => {}}
								/>
							</Flex>
							<Text size="small" color="color.text.subtle">
								Powered by Rovo AI. Verify results.
							</Text>
						</Flex>
					</PanelDisclaimer>
				}
			>
				<Button appearance="primary" onClick={() => {}}>
					Save
				</Button>
				<Checkbox label="Create another" isChecked={false} onChange={() => {}} />
			</PanelFooter>
		</PanelContainer>
	);
};

export default SidePanelExample;
